import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@sib/auth';
import { Loadable } from '@sib/ui';
import { Login } from '@spendbrain/auth/pages/LoginPage';
import { DefaultLayout, PageNotFound } from '@spendbrain/common/components';
import { EmptyPagePlaceholder } from '@spendbrain/common/components/EmptyPagePlaceholder';
import { paths } from '@spendbrain/config/paths';
import {
  usePowerBiReports,
  useSpendbrainRoutesAccess,
} from '@spendbrain/hooks';
import Cookie from 'js-cookie';

export const Router = () => {
  const layout = Cookie.get('react-resizable-panels:layout');
  const collapsed = Cookie.get('react-resizable-panels:collapsed');
  const defaultLayout = layout ? JSON.parse(layout) : undefined;
  const defaultCollapsed = collapsed ? JSON.parse(collapsed) : false;

  const { powerBiReports } = usePowerBiReports();
  const showReportsDirectory = powerBiReports.length > 0;
  const { isRouteAvailable } = useSpendbrainRoutesAccess();

  return (
    <Routes>
      <Route path={paths.login} element={<Login />} />
      <Route
        element={
          <ProtectedRoute fallbackPath={paths.login}>
            <DefaultLayout
              defaultLayout={defaultLayout}
              defaultCollapsed={defaultCollapsed}
              attribute="class"
              defaultTheme="system"
              enableSystem
              disableTransitionOnChange
              footer={
                <div className="flex flex-col-reverse items-center justify-between gap-x-4 gap-y-2 sm:flex-row sm:gap-y-0">
                  <div className="flex flex-1 items-center justify-start gap-x-4 text-sm"></div>
                  <div className="flex flex-1 flex-col items-start justify-start gap-x-4 gap-y-1 sm:flex-row sm:justify-end sm:gap-y-0"></div>
                </div>
              }
              navCollapsedSize={4}
              logo={{
                default: (
                  <>
                    <div className="inline-flex items-center gap-x-4">
                      <img
                        src="/images/sibLogo.webp"
                        alt="Refine"
                        className="h-8 w-8"
                      />
                      <h3 className="text-xl font-bold">SIB Admin</h3>
                    </div>
                  </>
                ),
                collapsed: <img src="/images/sibLogo.webp" alt="Refine" />,
              }}
            >
              <Outlet />
            </DefaultLayout>
          </ProtectedRoute>
        }
      >
        <Route
          path={paths.subsidiaryOrganization}
          element={
            <Loadable
              component={() =>
                import('@spendbrain/modules/subsidiaryOrganization')
              }
            />
          }
        />
        {isRouteAvailable(paths.dashboard) && (
          <Route
            path={paths.dashboard}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/dashboard')}
              />
            }
          />
        )}
        {isRouteAvailable(paths.documents.root) && (
          <Route
            path={`${paths.documents.root}/*`}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/documents')}
              />
            }
          />
        )}
        {isRouteAvailable(paths.locations) && (
          <Route
            path={paths.locations}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/locations')}
              />
            }
          />
        )}
        {showReportsDirectory && isRouteAvailable(paths.reports) && (
          <Route
            path={paths.reports}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/reports')}
              />
            }
          />
        )}
        {isRouteAvailable(paths.billing) && (
          <Route
            path={paths.billing}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/billing')}
              />
            }
          />
        )}
        {isRouteAvailable(paths.vendors) && (
          <Route
            path={paths.vendors}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/vendors')}
              />
            }
          />
        )}
        {isRouteAvailable(paths.savings) && (
          <Route
            path={paths.savings}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/savings')}
              />
            }
          />
        )}
        {isRouteAvailable(paths.services) && (
          <Route
            path={paths.services}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/services')}
              />
            }
          />
        )}
        {(isRouteAvailable(paths.tickets.employee.any) ||
          isRouteAvailable(paths.tickets.client.root)) && (
          <>
            <Route
              path={paths.tickets.employee.any}
              element={
                <Loadable
                  component={() => import('@spendbrain/modules/tickets')}
                />
              }
            />
            <Route
              path={paths.tickets.client.root}
              element={
                <Loadable
                  component={() => import('@spendbrain/modules/tickets')}
                />
              }
            />
          </>
        )}
        {isRouteAvailable(paths.yourTeam) && (
          <Route
            path={paths.yourTeam}
            element={
              <Loadable
                component={() => import('@spendbrain/modules/yourTeam')}
              />
            }
          />
        )}
        <Route
          path={paths.help}
          element={
            <Loadable
              component={() =>
                import('@spendbrain/modules/help/pages/GuidesPage')
              }
            />
          }
        />
        <Route path={paths.upcomingEvents} element={<EmptyPagePlaceholder />} />
        <Route path={paths.chat} element={<EmptyPagePlaceholder />} />
        <Route path={paths.account} element={<EmptyPagePlaceholder />} />
        <Route path={paths.account} element={<EmptyPagePlaceholder />} />
        <Route path={paths.billPay} element={<EmptyPagePlaceholder />} />
        <Route path={paths.contractTerms} element={<EmptyPagePlaceholder />} />
        <Route path={paths.any} element={<PageNotFound />} />
      </Route>
      <Route index element={<Navigate to={paths.dashboard} />} />
    </Routes>
  );
};
